import { InfoIcon } from 'lucide-react';
import { Controller, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import Link from '@/components/atoms/Link/Link';
import { ReminderTimeSelect } from '@/components/atoms/ReminderTimeSelect/ReminderTimeSelect';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { selectSelectedAction } from '@/redux/dialogs/selectors';
import { updateAction, updateNode } from '@/redux/nodes/actions';
import { ReminderAction } from '@/shared/types/action';
import { RootState } from '@/shared/types/state';
import { INACTIVITY_TIMEOUT_DOCS_URL } from '@/util/constants';
import { convertMinutesToSeconds, formatSecondsToReadable } from '@/util/dateUtils';
import { createYupResolver } from '@/util/forms/createYupResolver';
import { reminderActionSchema } from '@/util/validation/schemas/action-schemas';

import ToolkitWrapper from '../ToolkitWrapper';

interface Form {
  reminder_seconds: number;
}

export const ToolkitInactivityTimeout = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const { actionId, reminder_seconds, trigger_node_id } = useSelector((state) => {
    const selectedAction = selectSelectedAction(state as RootState) as ReminderAction;

    const { action_id: actionId, trigger_node_id } = selectedAction;
    // Backward compatibility: some actions may still have 'minutes' field from legacy data
    const legacyAction = selectedAction as ReminderAction & { minutes?: number };
    const seconds =
      selectedAction.reminder_seconds ?? convertMinutesToSeconds(legacyAction.minutes ?? 0);

    return { actionId, reminder_seconds: seconds, trigger_node_id };
  }, shallowEqual);

  const { control } = useForm<Form>({
    mode: 'onChange',
    values: { reminder_seconds },
    resolver: createYupResolver<Form>(reminderActionSchema),
  });

  return (
    <ToolkitWrapper type="reminder">
      <div className="flex flex-col gap-1">
        <Controller
          name="reminder_seconds"
          control={control}
          render={({ field, fieldState }) => (
            <>
              <ReminderTimeSelect
                value={field.value}
                onChange={(value) => {
                  const reminder_seconds = parseInt(value);
                  field.onChange(reminder_seconds);

                  dispatch(updateAction({ actionId, action: { reminder_seconds } }));

                  // Update the respective Reminder node
                  dispatch(
                    updateNode({
                      nodeId: trigger_node_id,
                      node: {
                        name: `${t('common.after')} ${formatSecondsToReadable(reminder_seconds)}`,
                      },
                    })
                  );
                }}
                hasError={!!fieldState.error}
                placeholder={t('ai_agents.reminder.select_duration')}
              />
              {fieldState.error && (
                <p className="ml-1 text-xs leading-5 font-normal text-destructive">
                  {fieldState.error.message}
                </p>
              )}
            </>
          )}
        />
      </div>

      <div className="mt-6">
        <Alert variant="info">
          <InfoIcon size={16} />
          <AlertDescription>
            <p>
              <Trans
                i18nKey="dialog.inactivity_timeout.banner_text"
                components={{ link1: <Link external href={INACTIVITY_TIMEOUT_DOCS_URL} /> }}
              />
            </p>
          </AlertDescription>
        </Alert>
      </div>
    </ToolkitWrapper>
  );
};
